import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Facebook, Instagram } from "lucide-react";
import { SOCIAL } from "@/lib/data";
import BrandMark from "@/components/BrandMark";
import StripeBar from "@/components/StripeBar";

const LINKS = [
  { label: "Home", path: "/" },
  { label: "Meet Dave", path: "/meet-dave" },
  { label: "Priorities", path: "/#priorities" },
  { label: "News & Press", path: "/news" },
  { label: "Get Involved", path: "/get-involved" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const isActive = (p) => {
    if (p.includes("#")) return location.pathname === "/" && location.hash === p.slice(p.indexOf("#"));
    if (p === "/") return location.pathname === "/" && !location.hash;
    return location.pathname.startsWith(p);
  };

  const linkClass = (p, mobile = false) => {
    const base = mobile
      ? "block px-6 py-4 font-display font-bold uppercase tracking-wider text-xl border-b border-cream/5"
      : "font-display font-bold uppercase tracking-wider text-base transition-colors duration-200";
    const active = mobile ? "text-gold bg-navy-light/40" : "text-gold";
    const idle = mobile ? "text-cream" : "text-cream/85 hover:text-cream";
    return `${base} ${isActive(p) ? active : idle}`;
  };

  return (
    <header className="sticky top-0 z-50 bg-navy shadow-lg shadow-navy-dark/40" data-testid="site-header">
      <StripeBar className="h-1 w-full" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center" data-testid="header-brand-link" onClick={() => setOpen(false)}>
            <BrandMark />
          </Link>

          <nav className="hidden lg:flex items-center gap-7" data-testid="header-nav">
            {LINKS.map((l) => (
              <Link key={l.path} to={l.path} data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={linkClass(l.path)}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={SOCIAL.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="header-facebook-link"
              className="hidden md:flex text-cream/70 hover:text-cream transition-colors"
            >
              <Facebook className="w-[18px] h-[18px]" />
            </a>
            <a
              href={SOCIAL.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="header-instagram-link"
              className="hidden md:flex text-cream/70 hover:text-cream transition-colors"
            >
              <Instagram className="w-[18px] h-[18px]" />
            </a>
            <Link
              to="/contribute"
              data-testid="header-contribute-button"
              className="bg-crimson hover:bg-crimson-dark text-white font-display font-extrabold uppercase tracking-wider text-base sm:text-lg px-5 sm:px-7 py-2.5 rounded-full transition-all duration-200 hover:scale-105 shadow-md shadow-crimson/30"
            >
              Contribute
            </Link>
            <button
              className="lg:hidden text-cream p-2"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden bg-navy-dark border-t border-cream/10" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={linkClass(l.path, true)}
            >
              {l.label}
            </Link>
          ))}
          <div className="flex items-center gap-6 px-6 py-5">
            <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="mobile-facebook-link" className="text-cream/80">
              <Facebook className="w-6 h-6" />
            </a>
            <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="mobile-instagram-link" className="text-cream/80">
              <Instagram className="w-6 h-6" />
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
